import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Lazy import to break circular dependency: api.js → store.js → prefetch.js → api.js
let clearPrefetchCache = null;
const getClearPrefetchCache = async () => {
  if (!clearPrefetchCache) {
    const prefetch = await import('./prefetch');
    clearPrefetchCache = prefetch.clearPrefetchCache;
  }
  return clearPrefetchCache;
};

// In-memory fallback storage for Safari private mode
let memoryStorage = {};

// Test if localStorage is available and working
const isLocalStorageAvailable = () => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

// Custom storage that handles Safari and mobile browser quirks
const customStorage = {
  getItem: (name) => {
    try {
      if (storageAvailable) {
        return localStorage.getItem(name);
      }
      return memoryStorage[name] || null;
    } catch (e) {
      console.warn('Storage getItem error:', e);
      return memoryStorage[name] || null;
    }
  },
  setItem: (name, value) => {
    try {
      if (storageAvailable) {
        localStorage.setItem(name, value);
      }
      // Always store in memory as backup
      memoryStorage[name] = value;
      
      // Also store token separately for API calls
      if (name === 'auth-storage') {
        try {
          const parsed = JSON.parse(value);
          if (parsed?.state?.token) {
            if (storageAvailable) {
              localStorage.setItem('auth_token', parsed.state.token);
            }
            memoryStorage['auth_token'] = parsed.state.token;
          }
        } catch (e) {}
      }
    } catch (e) {
      console.warn('Storage setItem error:', e);
      memoryStorage[name] = value;
    }
  },
  removeItem: (name) => {
    try {
      if (storageAvailable) {
        localStorage.removeItem(name);
      }
      delete memoryStorage[name];
      
      if (name === 'auth-storage') {
        if (storageAvailable) {
          localStorage.removeItem('auth_token');
        }
        delete memoryStorage['auth_token'];
      }
    } catch (e) {
      console.warn('Storage removeItem error:', e);
      delete memoryStorage[name];
    }
  },
};

// Helper to get auth token from any available source
export const getAuthToken = () => {
  try {
    // Try memory storage first (most reliable for recent auth)
    if (memoryStorage['auth_token']) {
      return memoryStorage['auth_token'];
    }
    
    // Try localStorage second
    if (storageAvailable) {
      const token = localStorage.getItem('auth_token');
      if (token) return token;
    }
    
    // Try Zustand store last
    const storeToken = useAuthStore.getState().token;
    if (storeToken) return storeToken;
    
    return null;
  } catch (e) {
    console.warn("getAuthToken error:", e);
    return memoryStorage['auth_token'] || null;
  }
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      setAuth: (user, token) => {
        console.log("[STORE] setAuth called with token:", token ? token.substring(0, 20) + "..." : "none");
        
        // IMMEDIATELY store token in all available storages before updating state
        try {
          if (storageAvailable) {
            localStorage.setItem('auth_token', token);
            // Verify it was saved
            const verify = localStorage.getItem('auth_token');
            console.log("[STORE] Token saved to localStorage, verified:", verify ? 'YES' : 'NO');
          } else {
            console.warn("[STORE] localStorage not available!");
          }
          memoryStorage['auth_token'] = token;
          console.log("[STORE] Token saved to memoryStorage");
        } catch (e) {
          console.warn("[STORE] Storage error, using memory only:", e);
          memoryStorage['auth_token'] = token;
        }
        
        // Now update Zustand state
        set({ user, token, isAuthenticated: true });
        console.log("[STORE] Zustand state updated, isAuthenticated: true");
        
        // Verify state was updated
        const state = get();
        console.log("[STORE] State after update - isAuthenticated:", state.isAuthenticated, "token:", state.token ? 'present' : 'null');
      },
      setUser: (user) => set({ user, isAuthenticated: true }),
      logout: () => {
        try {
          if (storageAvailable) {
            localStorage.removeItem('auth_token');
          }
          delete memoryStorage['auth_token'];
          // Clear prefetch cache on logout (lazy loaded to break circular import)
          getClearPrefetchCache().then(fn => fn && fn()).catch(() => {});
        } catch (e) {
          delete memoryStorage['auth_token'];
        }
        set({ user: null, token: null, isAuthenticated: false });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => customStorage),
      // Skip hydration issues on Safari
      skipHydration: false,
    }
  )
);

export const useVideoStore = create((set) => ({
  videos: [],
  pendingVideos: [],
  setVideos: (videos) => set({ videos }),
  setPendingVideos: (pendingVideos) => set({ pendingVideos }),
  addVideo: (video) => set((state) => ({ videos: [video, ...state.videos] })),
  updateVideo: (id, updates) =>
    set((state) => ({
      videos: state.videos.map((v) => (v.id === id ? { ...v, ...updates } : v)),
      pendingVideos: state.pendingVideos.map((v) =>
        v.id === id ? { ...v, ...updates } : v
      ),
    })),
  removeVideo: (id) =>
    set((state) => ({
      videos: state.videos.filter((v) => v.id !== id),
      pendingVideos: state.pendingVideos.filter((v) => v.id !== id),
    })),
}));

export const useTrendStore = create((set) => ({
  trends: [],
  setTrends: (trends) => set({ trends }),
}));
